import { EMPTY_INPUT } from "../simulation";
import type {
  ActionInput,
  MatchState,
  PlayerState,
  ProjectileState,
} from "../simulation";

const REACTION_TICKS = 8;

function cloneEmptyInput(): ActionInput {
  return { ...EMPTY_INPUT };
}

function sign(value: number): -1 | 0 | 1 {
  return value < -4 ? -1 : value > 4 ? 1 : 0;
}

function distanceSquared(a: PlayerState, b: PlayerState): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function nearest(
  player: PlayerState,
  candidates: PlayerState[],
): PlayerState | undefined {
  let result: PlayerState | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const candidateDistance = distanceSquared(player, candidate);
    if (candidateDistance < bestDistance) {
      result = candidate;
      bestDistance = candidateDistance;
    }
  }
  return result;
}

function hashUnit(seed: number, key: string, decision: number): number {
  let value = (seed ^ decision) >>> 0;
  for (let index = 0; index < key.length; index += 1) {
    value = Math.imul(value ^ key.charCodeAt(index), 2_246_822_519) >>> 0;
  }
  value ^= value >>> 13;
  value = Math.imul(value, 3_266_489_917) >>> 0;
  value ^= value >>> 16;
  return value / 4_294_967_296;
}

function threateningProjectile(
  player: PlayerState,
  projectiles: ProjectileState[],
): ProjectileState | undefined {
  return projectiles.find((projectile) => {
    if (projectile.team === player.team) {
      return false;
    }
    const dx = player.x - projectile.x;
    const approaching = dx * projectile.vx > 0;
    return approaching && Math.abs(dx) < 190 && Math.abs(player.y - projectile.y) < 75;
  });
}

function movementToward(player: PlayerState, target: PlayerState): ActionInput {
  const verticalDelta = target.y - player.y;
  return {
    move: sign(target.x - player.x),
    jump: verticalDelta < -70 && player.onGround,
    drop:
      verticalDelta > 80 &&
      player.onGround &&
      player.standingPlatformId !== null &&
      player.standingPlatformId !== "floor",
    fire: false,
  };
}

/**
 * Produces the same public ActionInput used by a human client. It intentionally
 * has no direct mutation path into the simulation and is deterministic for an
 * identical snapshot.
 */
export function createBotInput(
  state: MatchState,
  playerId: string,
): ActionInput {
  const player = state.players.find((candidate) => candidate.id === playerId);
  if (
    player === undefined ||
    state.phase !== "playing" ||
    player.status === "respawning"
  ) {
    return cloneEmptyInput();
  }

  const activeAllies = state.players.filter(
    (candidate) =>
      candidate.id !== player.id &&
      candidate.team === player.team &&
      candidate.status === "active",
  );

  if (player.status === "captured") {
    const ally = nearest(player, activeAllies);
    return ally === undefined
      ? cloneEmptyInput()
      : {
          move: sign(ally.x - player.x),
          jump: false,
          drop: false,
          fire: false,
        };
  }

  const capturedAlly = nearest(
    player,
    state.players.filter(
      (candidate) =>
        candidate.team === player.team && candidate.status === "captured",
    ),
  );
  if (capturedAlly !== undefined) {
    return movementToward(player, capturedAlly);
  }

  const capturedOpponent = nearest(
    player,
    state.players.filter(
      (candidate) =>
        candidate.team !== player.team && candidate.status === "captured",
    ),
  );
  if (capturedOpponent !== undefined) {
    return movementToward(player, capturedOpponent);
  }

  const danger = threateningProjectile(player, state.projectiles);
  if (danger !== undefined) {
    const projectileIsLeft = danger.x < player.x;
    return {
      move: projectileIsLeft ? 1 : -1,
      jump: player.onGround,
      drop: false,
      fire: false,
    };
  }

  const opponent = nearest(
    player,
    state.players.filter(
      (candidate) =>
        candidate.team !== player.team && candidate.status === "active",
    ),
  );
  if (opponent === undefined) {
    return {
      move: sign(state.wallInset + 640 - player.x),
      jump: false,
      drop: false,
      fire: false,
    };
  }

  const input = movementToward(player, opponent);
  const dx = opponent.x - player.x;
  const dy = Math.abs(opponent.y - player.y);
  const decision = Math.floor(state.tick / REACTION_TICKS);
  const aimConfidence = hashUnit(state.seed, player.id, decision);
  const facingTarget = sign(dx) === player.facing || Math.abs(dx) < 12;
  input.fire =
    player.fireCooldownTicks === 0 &&
    Math.abs(dx) < 430 &&
    dy < 85 &&
    facingTarget &&
    aimConfidence >= 0.2;

  // A small, seeded hesitation keeps the normal bot readable and avoids
  // frame-perfect pursuit without granting it information or actions humans lack.
  if (aimConfidence < 0.08) {
    input.move = 0;
  }
  return input;
}
